import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const CustomStatusDropdown = ({
  value,
  options = [],
  onChange,
  disabled = false,
  isArchived = false,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  // Get current selected option
  const currentOption = options.find((opt) => opt.value === value);
  const displayLabel = currentOption?.label || 'Select status';

  // Get status color based on value
  const getStatusColor = (statusValue) => {
    const statusColorMap = {
      'to-do': { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', dot: 'bg-gray-500', border: 'border-gray-300 dark:border-gray-600' },
      'in-progress': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500', border: 'border-blue-300 dark:border-blue-600' },
      'in-review': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', dot: 'bg-purple-500', border: 'border-purple-300 dark:border-purple-600' },
      'done': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', dot: 'bg-green-500', border: 'border-green-300 dark:border-green-600' },
      'archive': { bg: 'bg-slate-100 dark:bg-slate-700', text: 'text-slate-700 dark:text-slate-300', dot: 'bg-slate-500', border: 'border-slate-300 dark:border-slate-600' },
      // Project status colors
      'new': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-500', border: 'border-yellow-300 dark:border-yellow-600' },
      'ongoing': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500', border: 'border-blue-300 dark:border-blue-600' },
      'completed': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', dot: 'bg-green-500', border: 'border-green-300 dark:border-green-600' },
      'cancelled': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500', border: 'border-red-300 dark:border-red-600' },
      // Project type colors
      'maintenance': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500', border: 'border-orange-300 dark:border-orange-600' },
      'one-time': { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-400', dot: 'bg-pink-500', border: 'border-pink-300 dark:border-pink-600' },
    };

    return statusColorMap[statusValue] || statusColorMap['to-do'];
  };

  const currentColor = getStatusColor(value);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || isLoading}
        className={`w-full h-8 px-3 border border-gray-300 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 bg-white dark:bg-slate-800 dark:text-white flex justify-center items-center gap-2 text-xs ${disabled || isLoading
          ? 'bg-white dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-500 cursor-not-allowed'
          : `hover:border-blue-400 dark:hover:border-blue-500`
          }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${currentColor.dot}`} />
          <span className="truncate">{isLoading ? 'Loading...' : displayLabel}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 shadow-md rounded-lg overflow-hidden"
        >
          <div className="max-h-64 overflow-y-auto">
            {options.length > 0 ? (
              options.map((option) => {
                const optionColor = getStatusColor(option.value);
                const isSelected = value === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                    className={`w-full px-2 py-2 text-left text-xs font-medium transition-all duration-150 flex items-center justify-between gap-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0 ${isSelected
                      ? `${optionColor.bg} ${optionColor.text} bg-opacity-100`
                      : `text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50`
                      }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${optionColor.dot}`} />
                      <span className="truncate">{option.label}</span>
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-6 text-center text-gray-500 dark:text-slate-400 text-sm">
                No columns available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomStatusDropdown;
